import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable, catchError, of, tap } from "rxjs";

import { environment } from "../../environments/environment";

export interface NextimeOrigin {
  id: string;
  name: string;
  street: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
}

export interface NextimeSite {
  id: number;
  name: string;
  timezone: string;
  leadTimeInHours: number;
  url: string;
  origins: NextimeOrigin[];
  shouldDisplayDeliveryDate: boolean;
  shouldAlignNextOrderDate: boolean;
}

@Injectable({
  providedIn: "root",
})
export class NextimeService {
  private nextimeApiUrl = environment.nextimeApiUrl;
  private nextimeApiKey = environment.nextimeApiKey;

  private nextimeSiteSubject = new BehaviorSubject<NextimeSite | null>(null);
  public nextimeSite$ = this.nextimeSiteSubject.asObservable();

  constructor(private httpClient: HttpClient) {}

  getSiteInfo(siteId: number): Observable<NextimeSite | null> {
    const endpoint = `${this.nextimeApiUrl}/sites/${siteId}`;
    const headers = this.createHeaders();

    return this.httpClient.get<NextimeSite>(endpoint, { headers }).pipe(
      tap((nextimeSite) => {
        if (nextimeSite) {
          this.nextimeSiteSubject.next(nextimeSite);
        }
      }),
      catchError((error) => {
        console.error("Nextime API error:", error);
        return of(null);
      })
    );
  }

  getCurrentSite(): NextimeSite | null {
    return this.nextimeSiteSubject.value;
  }

  getShouldDisplayDeliveryDate(): boolean {
    const site = this.getCurrentSite();
    return site?.shouldDisplayDeliveryDate ?? true;
  }

  getShouldAlignNextOrderDate(): boolean {
    const site = this.getCurrentSite();
    return site?.shouldAlignNextOrderDate ?? true;
  }

  isCalendarDisabled(): boolean {
    return !this.getShouldDisplayDeliveryDate() || !this.getShouldAlignNextOrderDate();
  }

  private createHeaders(): HttpHeaders {
    return new HttpHeaders({
      "nextime-apikey": this.nextimeApiKey,
      "Accept": "application/json",
    });
  }
}
